import {convertSecondsToMinutesFloat, getTimeDifferenceInMinutes, convertMinutesToSeconds} from '@utils/momentWrapper';
import {convertToInt} from '@utils/helpers';
import {
    EVENT_CONTENT_VARIANT_STANDARD_FEED,
    INCIDENT_DURATION_UNIT_MINUTE,
    INCIDENT_DURATION_UNIT_PERCENT,
    REPORT_TYPE_INCIDENT,
} from '@constants/variables';

export const getEmptyReportData = () => ({
    reportType: '',
    reportScope: '',
    incidentSource: '',
    incidentType: '',
    incidentDuration: '',
    countAsDowntime: '',
    comment: '',
});

export const isIncidentReport = (report) => {
    const reportTypeId = report?.report_type?.id ?? report?.report_type_id,
        reportTypeName = report?.report_type_name ?? '';

    return REPORT_TYPE_INCIDENT === convertToInt(reportTypeId) || 'Incident' === reportTypeName;
};

export const parseReportDuration = (report, eventDuration) => {
    if (!report?.incident_duration) {
        return '';
    }
    const durationUnitId = convertToInt(
        report.incident_duration_unit_id ?? report.incident_duration_unit?.id
    );
    const durationValue = report.incident_duration_value ?? report.incident_duration;

    if (!durationUnitId && 'string' === typeof report.incident_duration) {
        return report.incident_duration;
    }

    if (durationUnitId === convertToInt(INCIDENT_DURATION_UNIT_MINUTE)) {
        if (eventDuration && 0 < eventDuration) {

            const percentage = (durationValue / convertMinutesToSeconds(eventDuration)?.toFixed()) * 100;

            return `${percentage?.toFixed(2)}%`;
        }

        return 'N/A';
    } else if (durationUnitId === convertToInt(INCIDENT_DURATION_UNIT_PERCENT)) {
        return `${durationValue?.toFixed(2)}%`;
    }

    return '';
};

export const getDistributionReportCoverage = (eventOrReportScope) => {
    if (!eventOrReportScope) return '';

    switch (true) {
        case eventOrReportScope.is_unreportable:
            return 'Unreportable';
        case eventOrReportScope.has_no_coverage:
            return 'None';
        case eventOrReportScope.has_partial_coverage:
            return 'Partial';
        case eventOrReportScope.has_full_coverage:
            return 'Full';
        default:
            return '';
    }
};

export const findReportsForDistribution = (reports, distributionId) => {
    if (!reports || !distributionId) return [];

    return reports.filter(report => {
        if (report.includes_all_streams) return true;

        if (report.report_scopes && Array.isArray(report.report_scopes)) {
            return report.report_scopes.some(scope =>
                convertToInt(scope.distribution_id) === convertToInt(distributionId)
            );
        }

        return false;
    });
};

export const formatReportScopeText = (report, options = {}) => {
    const { includeVariantSuffix = false } = options;

    if (report.includes_all_streams) {
        return 'All streams';
    }

    if (report.report_scopes_view_text) {
        return report.report_scopes_view_text;
    }

    if (report.report_scopes_view && Array.isArray(report.report_scopes_view)) {
        return report.report_scopes_view.map(scope => {
            let text = scope.text || '';

            if (includeVariantSuffix) {
                const contentVariantShortName =
                    convertToInt(scope.event_content_variant?.id) !== EVENT_CONTENT_VARIANT_STANDARD_FEED
                        ? (' - ' + scope.event_content_variant?.short_name)
                        : '';
                text = `${text}${contentVariantShortName}`;
            }

            return text;
        }).join(' | ');
    }

    return '';
};

export const parseCountAsDowntime = (countAsDowntime) => {
    if (true === countAsDowntime || 'Yes' === countAsDowntime) return 'Yes';

    if (false === countAsDowntime || '' === countAsDowntime) return 'No';

    return countAsDowntime || '';
};

export const collectAllDistributions = (eventContents) => {
    const allDistributions = [];

    if (!eventContents) return allDistributions;

    eventContents.forEach(eventContent => {
        const products = eventContent.products || [];
        products.forEach(product => {
            const distributions = product.distributions || [];

            if (0 < distributions.length) {
                distributions.forEach(distribution => {
                    allDistributions.push({
                        eventContent,
                        product,
                        distribution,
                    });
                });
            } else {
                allDistributions.push({
                    eventContent,
                    product,
                    distribution: null,
                });
            }
        });
    });

    return allDistributions;
};

export const getMatchDuration = (duration, startedDatetime, endedDatetime) => {
    if (duration) return convertSecondsToMinutesFloat(duration);

    if (startedDatetime && endedDatetime) return getTimeDifferenceInMinutes(endedDatetime, startedDatetime);

    return null;
};
